import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Quote } from "lucide-react";

export default function Bienvenida() {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/welcome-info`, { withCredentials: true })
      .then((res) => setInfo(res.data))
      .catch(() => setInfo({}));
  }, []);

  const firstName = (user?.name || "").split(" ")[0];

  return (
    <div className="min-h-full bg-gradient-to-b from-slate-50 to-cyan-50/40" data-testid="bienvenida-page">
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16">
        {/* Logo + saludo */}
        <div className="flex flex-col items-center text-center mb-12">
          <img src="/pd-travel-logo.png" alt="PD Travel" className="h-20 md:h-24 object-contain mb-6" data-testid="bienvenida-logo" />
          <p className="text-sm uppercase tracking-[0.25em] text-cyan-700 font-semibold mb-3">Back Office</p>
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-800" style={{ fontFamily: "'Mulish', sans-serif" }}>
            {firstName ? `Bienvenido, ${firstName}` : "Bienvenido"}
          </h1>
        </div>

        {/* Mensaje del Director Corporativo */}
        <div className="bg-white rounded-2xl shadow-xl shadow-cyan-900/5 border border-slate-100 overflow-hidden" data-testid="mensaje-director">
          <div className="h-1.5 bg-gradient-to-r from-cyan-700 via-cyan-500 to-blue-600" />
          <div className="p-8 md:p-12">
            <Quote className="w-8 h-8 text-cyan-600/40 mb-4" />
            <h2 className="text-xl md:text-2xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Mulish', sans-serif" }}>
              Bienvenido al Back Office de PD Travel.
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed text-[15px]">
              <p>
                Esta herramienta la construimos nosotros y para nosotros. Después de más de 20 años organizando
                viajes y eventos de negocios aprendimos algo: los detalles no se persiguen, se anticipan.
                Un pasaporte por vencer, un seguro sin emitir, un vuelo que cambió de hora. Antes todo eso vivía
                en correos y en la memoria de cada quien. Hoy vive aquí.
              </p>
              <p>
                Empezamos siendo un equipo pequeño que resolvía todo a mano. Hoy operamos cotizaciones,
                itinerarios, perfiles de viajero y alertas desde un solo lugar, con un sistema que revisa la
                operación las 24 horas para que tú trabajes con certidumbre. Ese salto no lo dio la tecnología
                sola. Lo dio la gente que está leyendo esto.
              </p>
              <p>
                Cada pantalla de este sistema existe porque alguien del equipo la pidió. Úsalo a diario y dinos
                qué le falta. Así hemos construido todo lo demás.
              </p>
              <p>Gracias por ser parte de este avance.</p>
            </div>
            <p className="mt-8 text-lg font-semibold text-cyan-700 italic" style={{ fontFamily: "'Mulish', sans-serif" }}>
              Cada viaje transforma.
            </p>
            <div className="mt-6 flex items-center gap-4">
              {info?.director_picture ? (
                <img src={info.director_picture} alt="Fernando Gómez" className="w-14 h-14 rounded-full object-cover ring-2 ring-cyan-600/30" referrerPolicy="no-referrer" data-testid="foto-director" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-cyan-700 text-white flex items-center justify-center font-bold text-lg">FG</div>
              )}
              <div>
                <p className="font-bold text-slate-800">Fernando Gómez</p>
                <p className="text-sm text-slate-500">Director Corporativo, PD Travel</p>
              </div>
            </div>
          </div>
        </div>

        {/* Dirección */}
        <div className="mt-10">
          <p className="text-center text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold mb-5">Nuestra Dirección</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5 flex items-center gap-4" data-testid="card-fernando">
              {info?.director_picture ? (
                <img src={info.director_picture} alt="Fernando Gómez" className="w-12 h-12 rounded-full object-cover" referrerPolicy="no-referrer" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-cyan-700 text-white flex items-center justify-center font-bold">FG</div>
              )}
              <div>
                <p className="font-bold text-slate-800 text-sm">Fernando Gómez</p>
                <p className="text-xs text-slate-500">Director Corporativo</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5 flex items-center gap-4" data-testid="card-ruben">
              {info?.ceo_picture ? (
                <img src={info.ceo_picture} alt="Rubén Mora" className="w-12 h-12 rounded-full object-cover" referrerPolicy="no-referrer" data-testid="foto-ceo" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-blue-800 text-white flex items-center justify-center font-bold">RM</div>
              )}
              <div>
                <p className="font-bold text-slate-800 text-sm">Rubén Mora</p>
                <p className="text-xs text-slate-500">CEO</p>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-12">PD Travel · Back Office</p>
      </div>
    </div>
  );
}
